import { Controller } from "@hotwired/stimulus";

// Reveals the full-width notes sub-row under a pricing line item on Review &
// Compare, per row or all at once. The sub-row is located as the price row's
// next sibling rather than by id, so it keeps working after the pricing section
// re-renders itself in place following a supplier selection or a note create.
//
// Every row has a sub-row, because the add-note form lives in it (#40519), so
// "Show all" acts only on the rows that actually carry a note — otherwise it
// would unfold a grid of empty forms.
export default class extends Controller {
  static targets = ["allButton", "allLabel"];
  static values = { reveal: Boolean };

  // A reader arriving from a bell or digest link (#40526) should land on the
  // note rather than on a collapsed grid. See rfx_reveal_notes? for why the
  // value is derived from the params rather than stored.
  connect() {
    if (this.revealValue) {
      this.setAllRows(true);
    }
  }

  toggleRow(event) {
    const priceRow = event.currentTarget.closest("tr");
    const notesRow = priceRow?.nextElementSibling;
    if (!notesRow?.hasAttribute("data-rfx-notes-row")) {
      return;
    }

    this.setRow(priceRow, notesRow, notesRow.hidden);
    this.syncAllControl();
  }

  toggleAll() {
    const rows = this.populatedRows();

    this.setAllRows(rows.some((notesRow) => notesRow.hidden), rows);
  }

  setAllRows(expand, rows = this.populatedRows()) {
    rows.forEach((notesRow) => this.setRow(notesRow.previousElementSibling, notesRow, expand));
    this.syncAllControl(rows);
  }

  setRow(priceRow, notesRow, expand) {
    notesRow.hidden = !expand;
    if (!priceRow) {
      return;
    }

    priceRow.classList.toggle("rfx-notes-open", expand);
    priceRow.querySelector("[data-rfx-notes-button]")?.setAttribute("aria-expanded", String(expand));
  }

  syncAllControl(rows = this.populatedRows()) {
    const allExpanded = rows.length > 0 && rows.every((notesRow) => !notesRow.hidden);

    if (this.hasAllButtonTarget) {
      this.allButtonTarget.setAttribute("aria-pressed", String(allExpanded));
    }
    if (this.hasAllLabelTarget) {
      this.allLabelTarget.textContent = allExpanded ? "Hide all notes" : "Show all notes";
    }
  }

  // The rows carrying an actual note, which is what "Show all" reveals. Every
  // row has a sub-row, so the marker is what separates them from empty forms.
  populatedRows() {
    return Array.from(this.element.querySelectorAll("[data-rfx-notes-row][data-rfx-has-notes]"));
  }
}
